import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  Directions as DirectionsIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { appConfig } from '../../config/app';

const RouteDetailsModal = ({ open, onClose, routeId, onRouteDeleted }) => {
  const [route, setRoute] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch route details
  const fetchRouteDetails = async () => {
    if (!routeId) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Fetch route details
      const routeResponse = await fetch(`${appConfig.api.baseURL}/delivery-routes/${routeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        }
      });

      if (!routeResponse.ok) {
        throw new Error(`Failed to fetch route details: ${routeResponse.status}`);
      }

      const routeData = await routeResponse.json();
      if (routeData.success) {
        setRoute(routeData.route);
        
        // Fetch order details for each order in the route
        if (routeData.route.orderIds && routeData.route.orderIds.length > 0) {
          const orderPromises = routeData.route.orderIds.map(async (orderId) => {
            try {
              const orderResponse = await fetch(`${appConfig.api.baseURL}/orders/${orderId}`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
                }
              });
              
              if (orderResponse.ok) {
                const orderData = await orderResponse.json();
                return orderData.success ? orderData.order : null;
              }
            } catch (error) {
              console.error(`Error fetching order ${orderId}:`, error);
            }
            return null;
          });
          
          const orderResults = await Promise.all(orderPromises);
          setOrders(orderResults.filter(order => order !== null));
        }
      } else {
        throw new Error(routeData.message || 'Failed to fetch route details');
      }
    } catch (error) {
      console.error('Error fetching route details:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Load route details when modal opens
  useEffect(() => {
    if (open && routeId) {
      fetchRouteDetails();
    }
  }, [open, routeId]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setRoute(null);
      setOrders([]);
      setError('');
    }
  }, [open]);

  // Format date/time for display
  const formatDateTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'created': return 'default';
      case 'assigned': return 'primary';
      case 'active': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  // Get status label
  const getStatusLabel = (status) => {
    switch (status) {
      case 'created': return 'Created';
      case 'assigned': return 'Assigned';
      case 'active': return 'Active';
      case 'completed': return 'Completed';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  };

  // Get order status color
  const getOrderStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'pending': return 'default';
      case 'confirmed': return 'info';
      case 'processing': return 'warning';
      case 'out_for_delivery': return 'primary';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };
  
  // Handle delete route
  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };
  
  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
  };
  
  const handleConfirmDelete = async () => {
    if (!route) return;
    
    setDeleting(true);
    try {
      // Step 1: Remove routeId from all orders in the route
      const ordersUpdateResponse = await fetch(`${appConfig.api.baseURL}/orders/bulk-update-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        },
        body: JSON.stringify({
          orderIds: route.orderIds || [],
          updateData: {
            routeId: null,
            deliveryPersonMobile: null,
            deliveryPersonName: null
          }
        })
      });
      
      if (!ordersUpdateResponse.ok) {
        throw new Error('Failed to update orders');
      }
      
      // Step 2: Delete the route
      const deleteResponse = await fetch(`${appConfig.api.baseURL}/delivery-routes/${route.routeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem(appConfig.storage.token)}`
        }
      });
      
      if (!deleteResponse.ok) {
        throw new Error('Failed to delete route');
      }
      
      // Close dialogs and notify parent
      setDeleteConfirmOpen(false);
      onClose();
      
      // Notify parent component that route was deleted
      if (onRouteDeleted) {
        onRouteDeleted(route.routeId);
      }
    } catch (error) {
      console.error('Error deleting route:', error);
      setError(error.message || 'Failed to delete route');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" component="div">
          Route Details
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : route ? (
          <Box>
            {/* Route Overview */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Route Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AssignmentIcon color="primary" />
                        <Typography variant="body2">
                          <strong>Route ID:</strong> {route.routeId}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip 
                          label={getStatusLabel(route.status)}
                          color={getStatusColor(route.status)}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScheduleIcon color="action" />
                        <Typography variant="body2">
                          <strong>Created:</strong> {formatDateTime(route.createdAt)}
                        </Typography>
                      </Box>
                      {route.assignedAt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ScheduleIcon color="action" />
                          <Typography variant="body2">
                            <strong>Assigned:</strong> {formatDateTime(route.assignedAt)}
                          </Typography>
                        </Box>
                      )}
                      {route.startedAt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DirectionsIcon color="action" />
                          <Typography variant="body2">
                            <strong>Started:</strong> {formatDateTime(route.startedAt)}
                          </Typography>
                        </Box>
                      )}
                      {route.completedAt && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DirectionsIcon color="success" />
                          <Typography variant="body2">
                            <strong>Completed:</strong> {formatDateTime(route.completedAt)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Delivery Person
                    </Typography>
                    {route.deliveryPersonName ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon color="primary" />
                          <Typography variant="body2">
                            <strong>Name:</strong> {route.deliveryPersonName}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon color="primary" />
                          <Typography variant="body2">
                            <strong>Mobile:</strong> {route.deliveryPersonMobile}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            <strong>ID:</strong> {route.deliveryPersonId}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No delivery person assigned
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Orders in Route */}
            <Typography variant="h6" gutterBottom>
              Orders in Route ({orders.length})
            </Typography>
            
            {orders.length > 0 ? (
              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Order ID</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Address</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Time Slot</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order, index) => (
                      <TableRow key={order.id || order.orderId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {order.orderId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            #{index + 1} in route
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2">
                            {order.deliveryAddress?.userName || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.userId}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationIcon fontSize="small" color="action" />
                            <Box>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {order.deliveryAddress?.houseDetails || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {order.deliveryAddress?.pincode || 'N/A'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2">
                            {order.timeSlotDetails?.startTime || 'N/A'} - {order.timeSlotDetails?.endTime || 'N/A'}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            ₹{order.total || 0}
                          </Typography>
                        </TableCell>
                        
                        <TableCell>
                          <Chip 
                            label={order.order_status || 'N/A'}
                            color={(order.order_status || '').toLowerCase() === 'assigned' ? undefined : getOrderStatusColor(order.order_status)}
                            size="small"
                            sx={
                              (order.order_status || '').toLowerCase() === 'assigned'
                                ? { bgcolor: '#2e7d32', color: '#fff', '& .MuiChip-label': { color: '#fff', textTransform: 'capitalize' } }
                                : { '& .MuiChip-label': { textTransform: 'capitalize' } }
                            }
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                No orders found for this route
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No route data available
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Button 
          onClick={handleDeleteClick} 
          color="error" 
          variant="outlined"
          startIcon={<DeleteIcon />}
          disabled={loading || deleting}
        >
          Delete Route
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Delete Route
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            Are you sure you want to delete this route?
          </Alert>
          {route && (
            <Box
              sx={{
                p: 2,
                backgroundColor: 'background.default',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <strong>Route ID:</strong> {route.routeId}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong> {getStatusLabel(route.status)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Orders:</strong> {route.orderIds?.length || 0} orders
              </Typography>
              {route.deliveryPersonName && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Assigned to:</strong> {route.deliveryPersonName}
                </Typography>
              )}
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone. The route will be permanently deleted and all orders will be unassigned from this route.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete Route'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default RouteDetailsModal;
